import {
	createFileRoute,
	Link,
	Navigate,
	redirect,
	useRouteContext,
} from "@tanstack/react-router";
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay";
import type { cursosQuery } from "./__generated__/cursosQuery.graphql";
import { Image } from "@/components/image";
import { CourseCategory } from "@/ui/course-category";
import { environment } from "@/relay/environment";
import type { cursosEnabledQuery } from "./__generated__/cursosEnabledQuery.graphql";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icon";
import { env } from "@/env";

export const Route = createFileRoute("/_visibility/_topnav/_tabs/cursos")({
	component: RouteComponent,
	pendingComponent: () => {
		return (
			<div className="size-full flex flex-col items-center justify-center py-12">
				<Spinner />
			</div>
		);
	},
	pendingMinMs: 300,
	loader: async ({ context }) => {
		const { club } = context;

		const data = await fetchQuery<cursosEnabledQuery>(
			environment,
			graphql`
		    query cursosEnabledQuery($slug: String!) {
		      club(slug: $slug) {
				    configuration(key: "COURSES_ENABLED")
			    }
		    }
	`,
			{ slug: club.slug },
		).toPromise();

		if (data?.club.configuration?.length && data?.club.configuration !== "1") {
			throw redirect({
				to: "/",
			});
		}
	},
	ssr: false,
});

function RouteComponent() {
	const { club, session } = useRouteContext({ from: "__root__" });

	const data = useLazyLoadQuery<cursosQuery>(
		graphql`
    query cursosQuery($club: String!) {
      club(slug: $club) {
        id

        coursePage {
          appearance {
            backgroundImage {
              signedUrl
            }
          }
          categories(status: ACTIVE) {
            id
            courses(status: ACTIVE) {
              canView
              visibility
            }
            ...courseCategoryFragment
          }
        }
      }
    }
  `,
		{ club: club.slug },
	);

	if (
		!club.permissions.length &&
		!data.club.coursePage.categories.some((c) =>
			c.courses.some(
				(c2) =>
					c2.visibility === "PRIVATE" ||
					c2.visibility === "PUBLIC" ||
					c2.visibility === "MEMBER_ONLY" ||
					c2.canView,
			),
		)
	) {
		return <Navigate to="/" />;
	}

	const hasCourses = data.club.coursePage.categories.some((c) =>
		c.courses.some(
			(c2) =>
				c2.visibility === "PRIVATE" ||
				c2.visibility === "PUBLIC" ||
				c2.visibility === "MEMBER_ONLY" ||
				c2.canView,
		),
	);

	const firstCategory = data.club.coursePage.categories[0];

	return (
		<div className="w-full overflow-x-hidden">
			{data.club.coursePage.appearance.backgroundImage ? (
				<div className="h-[calc(100dvw/4.26)] w-dvw">
					<Image
						src={data.club.coursePage.appearance.backgroundImage.signedUrl}
						className="object-cover h-[calc(100dvw/4.26)] w-dvw"
						width={1450}
						quality={95}
					/>
				</div>
			) : null}

			{hasCourses ? (
				<div className="flex items-center justify-start flex-col">
					{data.club.coursePage.categories.map((cat) => {
						if (
							!cat.courses.some((c) => c.canView || c.visibility !== "SECRET")
						) {
							return null;
						}

						return <CourseCategory key={cat.id} category={cat} />;
					})}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center py-6">
					<div className="w-full flex items-center justify-center gap-6 flex-col py-6 px-6">
						<div className="flex flex-col gap-1.5 items-center">
							<h1 className="text-2xl font-semibold text-center">
								Crie seu primeiro Curso
							</h1>

							<span className="text-muted-foreground text-center">
								Comece sua jornada na Scarf com seu primeiro curso
							</span>
						</div>

						<Link
							to={
								firstCategory
									? `${env.VITE_ADMIN_URL}/c/${club.slug}/courses/new?token=${session?.refreshToken}&category=${firstCategory.id}`
									: `${env.VITE_ADMIN_URL}/c/${club.slug}/courses?token=${session?.refreshToken}`
							}
							target="_blank"
						>
							<Button className="flex items-center">
								<Icon name="Plus" />
								Criar Curso
							</Button>
						</Link>
					</div>
				</div>
			)}
		</div>
	);
}
